// ═══════════════ RESERVO VEHICLE TIER CLASSIFICATION ═══════════════
// Auto-assigns tier based on make, model, and passenger capacity
// Similar to Uber's UberX / Comfort / XL / Black tier system
//
// Tiers:
//   go    — Standard sedans and hatchbacks (up to 4 pax)
//   comfy — Premium sedans, mid-range SUVs (up to 4 pax)
//   plus  — Large SUVs, people movers, vans (5-8 pax)
//   vip   — Luxury vehicles from premium brands (up to 4 pax)

// ═══════════════ VIP-ONLY BRANDS ═══════════════
// Any vehicle from these brands is automatically VIP
const VIP_BRANDS = [
  'Aston Martin',
  'Bentley',
  'Ferrari',
  'Lamborghini',
  'Maserati',
  'McLaren',
  'Rolls-Royce',
];

// ═══════════════ PREMIUM BRANDS (Comfy or VIP depending on model) ═══════════════
const PREMIUM_BRANDS = ['Mercedes-Benz', 'BMW', 'Audi', 'Lexus', 'Genesis', 'Porsche', 'Volvo', 'Jaguar', 'Land Rover'];

// ═══════════════ VIP-ELIGIBLE MODELS FROM PREMIUM BRANDS ═══════════════
// These specific models from premium brands qualify for VIP
const VIP_MODELS = {
  'Mercedes-Benz': ['S-Class', 'E-Class', 'EQS', 'EQS SUV', 'AMG GT', 'AMG S63', 'AMG E63', 'GLS', 'V-Class', 'Maybach'],
  'BMW': ['7 Series', '8 Series', 'i7', 'M8', 'X7', 'XM'],
  'Audi': ['A8', 'A7', 'e-tron GT', 'RS6', 'RS7', 'Q8', 'RS Q8', 'SQ8'],
  'Lexus': ['LS', 'LC', 'LM', 'LX'],
  'Genesis': ['G90', 'GV80', 'GV80 Coupe'],
  'Porsche': ['Panamera', 'Panamera E-Hybrid', 'Cayenne', 'Cayenne Coupe', 'Cayenne E-Hybrid', 'Taycan', 'Taycan Cross Turismo', 'Taycan Sport Turismo'],
  'Volvo': ['S90', 'V90', 'V90 Cross Country', 'XC90', 'XC90 Recharge', 'EX90'],
  'Jaguar': ['XJ', 'F-Pace SVR', 'I-Pace'],
  'Land Rover': ['Range Rover', 'Range Rover Sport'],
};

// ═══════════════ VIP PLUS MODELS (luxury large vehicles) ═══════════════
const VIP_PLUS_MODELS = {
  'Mercedes-Benz': ['GLS', 'AMG GLE63', 'EQS SUV', 'V-Class', 'EQV', 'Maybach'],
  'BMW': ['X7', 'XM', 'i7'],
  'Audi': ['Q7', 'Q8', 'Q8 e-tron', 'SQ7', 'SQ8', 'RS Q8'],
  'Lexus': ['LX', 'TX', 'LM'],
  'Land Rover': ['Range Rover', 'Range Rover Sport', 'Defender 130'],
  'Porsche': ['Cayenne', 'Cayenne Coupe', 'Cayenne E-Hybrid'],
  'Volvo': ['XC90', 'XC90 Recharge', 'EX90'],
  'Genesis': ['GV80', 'GV80 Coupe'],
  'Bentley': ['Bentayga'],
  'Rolls-Royce': ['Cullinan'],
  'Lamborghini': ['Urus', 'Urus Performante', 'Urus SE'],
  'Maserati': ['Levante'],
};

// ═══════════════ PLUS-TIER MODELS (large vehicles, 5-8 pax) ═══════════════
// Models that are large SUVs, people movers, or vans — always Plus tier
const PLUS_MODELS = {
  'Toyota': ['Kluger', 'Kluger Hybrid', 'Land Cruiser', 'Land Cruiser 300', 'Land Cruiser 250', 'Prado', 'Granvia', 'HiAce', 'Tarago', 'Alphard', 'Vellfire'],
  'Hyundai': ['Palisade', 'Staria', 'Staria-Load', 'Santa Fe', 'Santa Fe Hybrid'],
  'Kia': ['Carnival', 'Sorento', 'Sorento Hybrid', 'EV9'],
  'Mazda': ['CX-9', 'CX-90', 'CX-80', 'CX-8'],
  'Mitsubishi': ['Pajero', 'Pajero Sport', 'Outlander', 'Outlander PHEV'],
  'Nissan': ['Patrol', 'Pathfinder'],
  'Ford': ['Everest', 'Explorer', 'Territory', 'Transit', 'Transit Custom'],
  'Volkswagen': ['Multivan', 'Caravelle', 'Touareg', 'Tiguan Allspace', 'Transporter'],
  'Mercedes-Benz': ['GLE', 'GLE Coupe', 'GLS', 'V-Class', 'Vito', 'Sprinter', 'EQV', 'AMG GLE53', 'AMG GLE63'],
  'BMW': ['X5', 'X6', 'X7', 'XM'],
  'Audi': ['Q7', 'Q8', 'Q8 e-tron', 'SQ7', 'SQ8', 'RS Q8'],
  'Lexus': ['LX', 'TX', 'GX', 'RX', 'RX Hybrid'],
  'Land Rover': ['Discovery', 'Defender 110', 'Defender 130', 'Range Rover', 'Range Rover Sport'],
  'Jeep': ['Grand Cherokee', 'Grand Cherokee L', 'Wrangler Unlimited', 'Commander'],
  'Isuzu': ['MU-X'],
  'LDV': ['D90', 'Deliver 9'],
  'Subaru': ['Outback'],
  'Volvo': ['XC90', 'XC90 Recharge', 'EX90'],
  'Genesis': ['GV80', 'GV80 Coupe'],
  'GWM': ['Tank 300', 'Tank 500'],
  'Tank': ['300', '500'],
  'Dodge': ['Ram 1500', 'Ram 2500', 'Ram 3500'],
  'Ram': ['1500', '1500 DS', '1500 DT', '2500', '3500'],
  'Tesla': ['Model X', 'Cybertruck'],
  'Porsche': ['Cayenne', 'Cayenne Coupe', 'Cayenne E-Hybrid'],
};

// ═══════════════ COMFY-TIER MODELS (premium but not VIP) ═══════════════
// Models that are a step above standard — nicer ride experience
const COMFY_MODELS = {
  'Toyota': ['Camry', 'Camry Hybrid', 'RAV4', 'RAV4 Hybrid', 'RAV4 Prime', 'C-HR Hybrid', 'Corolla Cross Hybrid', 'GR Supra', 'Supra'],
  'Hyundai': ['Tucson', 'Tucson Hybrid', 'Ioniq 5', 'Ioniq 5 N', 'Ioniq 6', 'Kona Electric', 'Kona N'],
  'Kia': ['EV6', 'EV6 GT', 'EV5', 'Sportage Hybrid', 'Stinger', 'Niro EV'],
  'Mazda': ['CX-60', 'CX-5', 'Mazda6', 'MX-5'],
  'Honda': ['CR-V', 'ZR-V', 'Accord', 'Civic Type R'],
  'Subaru': ['Forester', 'Forester Hybrid', 'Crosstrek', 'Solterra', 'Levorg', 'WRX'],
  'Volkswagen': ['Golf GTI', 'Golf R', 'Tiguan', 'T-Roc R', 'Arteon', 'Passat', 'ID.4', 'ID.5', 'ID.7'],
  'Skoda': ['Superb', 'Kodiaq', 'Enyaq', 'Octavia'],
  'Peugeot': ['408', '508', '3008', 'e-3008', '5008', 'e-5008'],
  'Tesla': ['Model 3', 'Model Y', 'Model S'],
  'Polestar': ['Polestar 2', 'Polestar 3', 'Polestar 4'],
  'BYD': ['Seal', 'Seal U', 'Tang'],
  'Cupra': ['Formentor', 'Born', 'Tavascan'],
  'Mercedes-Benz': ['A-Class', 'B-Class', 'C-Class', 'CLA', 'CLE', 'GLA', 'GLB', 'GLC', 'GLC Coupe', 'EQA', 'EQB', 'EQC', 'EQE', 'EQE SUV', 'AMG A35', 'AMG A45', 'AMG C43', 'AMG C63', 'AMG CLA35', 'AMG CLA45', 'AMG E53', 'AMG GLA35', 'AMG GLA45', 'AMG GLC43', 'AMG GLC63'],
  'BMW': ['1 Series', '2 Series', '3 Series', '4 Series', '5 Series', '6 Series GT', 'i4', 'i5', 'iX', 'iX1', 'iX2', 'iX3', 'M2', 'M3', 'M4', 'M5', 'X1', 'X2', 'X3', 'X4', 'Z4'],
  'Audi': ['A3', 'A4', 'A5', 'A6', 'Q2', 'Q3', 'Q4 e-tron', 'Q5', 'e-tron', 'RS3', 'RS4', 'RS5', 'RS Q3', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'SQ5', 'TT', 'TT RS'],
  'Lexus': ['ES', 'IS', 'IS F Sport', 'NX', 'NX Hybrid', 'RC', 'RC F', 'UX', 'UX Hybrid', 'LC Convertible'],
  'Genesis': ['G70', 'G80', 'G80 Electrified', 'GV60', 'GV70', 'GV70 Electrified'],
  'Volvo': ['S60', 'V60', 'V60 Cross Country', 'XC40', 'XC40 Recharge', 'XC60', 'XC60 Recharge', 'C40 Recharge', 'EX30', 'EX40'],
  'Jaguar': ['E-Pace', 'F-Pace', 'F-Type', 'XE', 'XF'],
  'Land Rover': ['Discovery Sport', 'Range Rover Evoque', 'Range Rover Velar', 'Defender', 'Defender 90'],
  'Porsche': ['718 Boxster', '718 Cayman', '911', '911 GT3', '911 Turbo', 'Macan', 'Macan Electric'],
  'Mini': ['Cooper S', 'JCW', 'JCW Countryman', 'Countryman SE', 'Electric'],
  'Lotus': ['Emira', 'Eletre', 'Emeya'],
  'Alfa Romeo': ['Giulia', 'Giulia Quadrifoglio', 'Stelvio', 'Stelvio Quadrifoglio', 'Tonale'],
  'Maserati': ['Ghibli', 'Grecale', 'Levante', 'GranTurismo', 'GranCabrio', 'MC20', 'MC20 Cielo', 'Quattroporte'],
  'Renault': ['Arkana', 'Austral', 'Megane E-Tech', 'Scenic E-Tech'],
  'Nissan': ['400Z', 'Qashqai e-Power', 'X-Trail e-Power', 'Leaf'],
  'Ford': ['Mustang', 'Mustang Mach-E', 'F-150 Lightning'],
  'MG': ['MG4', 'MG7'],
  'Smart': ['#1', '#3'],
  'Zeekr': ['001', '007', '009', 'X'],
};

// ═══════════════ CLASSIFICATION FUNCTION ═══════════════
export function classifyVehicleTier(make, model) {
  if (!make) return null;

  // Rule 1: Super-luxury brands are always VIP
  if (VIP_BRANDS.includes(make)) return 'vip';

  // Rule 2: VIP Plus models (luxury SUVs)
  if (VIP_PLUS_MODELS[make]?.includes(model)) return 'vipPlus';

  // Rule 3: Specific VIP models from premium brands
  if (VIP_MODELS[make]?.includes(model)) return 'vip';

  // Rule 4: Check Plus tier (large vehicles)
  if (PLUS_MODELS[make]?.includes(model)) return 'plus';


  // Rule 4: Check Comfy tier (premium vehicles)
  if (COMFY_MODELS[make]?.includes(model)) return 'comfy';

  // Rule 5: Premium brand but model not specifically listed = Comfy
  if (PREMIUM_BRANDS.includes(make)) return 'comfy';

  // Rule 6: Everything else is Go (standard)
  return 'go';
}

// ═══════════════ TIER DISPLAY INFO ═══════════════
export const TIER_INFO = {
  go:      { name: 'Reservo Go',    desc: 'Standard sedan — affordable everyday rides', maxPax: 4, colour: '#2dd4a8' },
  comfy:   { name: 'Reservo Comfy', desc: 'Premium vehicle — a more comfortable ride', maxPax: 4, colour: '#6366f1' },
  plus:    { name: 'Reservo Plus',  desc: 'Large vehicle — extra space for groups', maxPax: 8, colour: '#fbbf24' },
  vip:     { name: 'Reservo VIP',   desc: 'Luxury sedan — top-tier vehicles only', maxPax: 4, colour: '#8b5cf6' },
  vipPlus: { name: 'Reservo VIP+',  desc: 'Luxury SUV — premium space & luxury combined', maxPax: 6, colour: '#ec4899' },
};

export default { classifyVehicleTier, TIER_INFO, VIP_BRANDS, PREMIUM_BRANDS };